import React from 'react';
import { motion } from 'framer-motion';
import Layout from '../../components/layout/layout';
import Button from '../../components/ui/button';
import { WebPageSchema } from '../../lib/utils/schema-utils';
import HeroTemplateEnts from '../../components/sections/HeroTemplateEnts';

const Campagnes = () => {
  return (
    <Layout>
      <WebPageSchema
        page={{
          title: "Campagnes de recrutement",
          description: "Une approche en tunnel RH, de l'identification à l'intégration, pensée pour recruter mieux et plus simplement.",
          url: "/campagnes"
        }}
      />

      {/* HERO SECTION - Blanc */}
      <HeroTemplateEnts
        title={
          <>
            Et si vos{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
              recrutements
            </span>{" "}
            suivaient un vrai plan ?
          </>
        }
        description={
          <>
            <p className="text-lg text-neutral-800">
              Nos campagnes s'appuient sur un funnel RH structuré pour{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254] font-semibold">
                attirer, filtrer, convaincre, et intégrer
              </span>
              … les bons profils, pas les bons CV.
            </p>
          </>
        }
        imageUrl="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/Campagnes-recrutement-marseille.webp"
        breadcrumbs={[
          { name: "Entreprise", href: "/entreprise" },
          { name: "Campagnes", href: "/entreprise/campagnes" }
        ]}
        cta1={{
          label: "Découvrir nos formules",
          href: "/infos/tarifs"
        }}
        cta2={{
          label: "Parler à un expert",
          href: "/infos/contact"
        }}
      />

      {/* ÉTAPE 1 - COULEUR JAUNE CLAIR */}
      <section className="py-32 px-6 bg-yellow-50">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-4 text-neutral-900">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                1. Cibler les bons profils, actifs ou passifs
              </span>
            </h2>
            <p className="text-lg mb-6 text-neutral-800">
              Créer un vivier de talents, c'est bâtir une communauté de candidats en affinité avec votre marque employeur. En les activant au bon moment (et avec une politique de cooptation rémunérée), vous êtes réactif, pertinent, et visible. Moins de stress, plus de réponses.
            </p>
            <ul className="list-disc list-inside space-y-2 text-neutral-700">
              <li>🎯 Ciblage omnicanal & multi-plateforme</li>
              <li>📊 Base activable + cooptation = recrutement accéléré</li>
              <li>🤝 Fidélisation à la marque employeur = candidats pré-engagés</li>
            </ul>
          </div>
          <img 
            src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/Vivier-de-talents.webp" 
            alt="Vivier de talents" 
            className="rounded-xl"
            style={{ background: "none" }}
          />
        </div>
      </section>

      {/* ÉTAPE 2 - FOND BLANC */}
      <section className="py-32 px-6 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6 text-gradient-primary">2. Engager les bons candidats, pas tous les candidats</h2>
          <p className="text-lg mb-12 max-w-2xl mx-auto text-neutral-800">
            Les talents ne postulent plus, ils choisissent. Avec une marque employeur vivante et des contenus adaptés, vous multipliez par 3 l'intérêt des profils ciblés (source : LinkedIn Talent Trends).
          </p>
          <div className="grid md:grid-cols-3 gap-6 text-left">
            {[{
              title: '📖 Contenu personnalisé',
              text: "Storytelling authentique, vidéos internes, culture d'équipe partagée."
            }, {
              title: '📱 Relances intelligentes',
              text: "WhatsApp, emails, CRM automation : multipliez les points de contact sans harcèlement."
            }, {
              title: '🧠 Parcours mesuré',
              text: "Dashboard d'engagement pour adapter le parcours en temps réel."
            }].map((item, i) => (
              <motion.div key={i} className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition duration-300">
                <h3 className="font-semibold text-lg mb-2 text-neutral-900">{item.title}</h3>
                <p className="text-neutral-700">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ÉTAPE 3 - COULEUR BLEU */}
      <section className="py-32 px-6 bg-blue-50">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-4 text-neutral-900">3. Filtrer sans frustrer</h2>
            <p className="text-lg mb-6 text-neutral-800">
              62% des candidats abandonnent un process s'ils ne se sentent pas considérés (Glassdoor). Préqualification, mapping RH, scoring comportemental : vous ciblez les bons, rapidement, sans pression. Gain de temps RH moyen : -40%.
            </p>
            <Button href="/mapping" className="text-lg">
              Découvrir notre stratégie 🧠
            </Button>
          </div>
          <img 
            src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/campagne-recrutement-marseille.webp" 
            alt="Matching RH" 
            className="w-full rounded-xl"
            style={{ background: "none" }}
          />
        </div>
      </section>

      {/* ÉTAPE 4 - FOND BLANC */}
      <section className="py-32 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4 text-gradient-primary">4. Préparer l'intégration, pas juste l'embauche</h2>
          <p className="text-lg mb-6 text-neutral-800">
            L'intégration commence avant le jour 1. Notre parcours gamifié, nos checklists RH et notre coach d'onboarding créent une montée en compétence + rapide et réduisent le turnover de 60% à 90 jours.
          </p>
          <ul className="list-disc list-inside text-left max-w-xl mx-auto space-y-2 text-neutral-700">
            <li>✅ Onboarding personnalisé dès la signature</li>
            <li>✅ Feedbacks RH et managers</li>
            <li>✅ Objectifs suivis semaine par semaine</li>
          </ul>
        </div>
      </section>

      {/* CTA FINAL - FOND PASTEL */}
      <section className="py-32 px-6 text-center bg-gradient-to-r from-yellow-100 via-pink-100 to-blue-100">
        <div className="inline-block p-8 rounded-2xl shadow-xl">
          <h2 className="text-2xl md:text-3xl font-bold mb-4 text-neutral-900">Prêt à structurer vos campagnes de recrutement ?</h2>
          <Button href="/contact" className="bg-black text-white text-lg">
            📞 Planifier un appel découverte
          </Button>
        </div>
      </section>

      {/* BLOC SEO FINAL */}
      <section className="bg-white py-16 px-6 md:px-16">
        <div className="max-w-4xl mx-auto text-lg text-neutral-800 leading-relaxed">
          <h2 className="text-3xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
            Une vraie campagne RH, pilotée comme un canal d'acquisition
          </h2>
          <p className="mb-4">
            Chez Recruteurs.io, on ne balance pas des annonces à la chaîne. On conçoit des <strong>campagnes de recrutement structurées</strong>, basées sur un funnel RH clair : <em>attirer → filtrer → qualifier → intégrer</em>. C'est notre cœur de métier, et on le fait avec les <strong>meilleurs outils du marché</strong>.
          </p>
          <p className="mb-4">
            🎯 <strong>1. On construit et anime votre vivier</strong> : à partir de vos bases internes, de LinkedIn, d'Indeed ou de campagnes sponsorisées, on bâtit un vivier segmenté, activable en un clic. Et surtout, on l'anime : emailings, séquences WhatsApp, contenus vidéos, storytelling marque employeur… tout est prêt.
          </p>
          <p className="mb-4">
            ✉️ <strong>2. Quand un poste s'ouvre, on dégaine</strong> : envois ciblés (email + SMS), appels sortants, cooptation rémunérée, RDV pris en ligne, validation par un consultant. Vous ne recevez que des profils motivés et triés. Tout est mesuré, suivi, tracé.
          </p>
          <p className="mb-4">
            💡 Et surtout : on sait manier les bons outils. CRM candidats, automation multicanal, ATS, IA de matching, dashboards RH, tunnel de conversion… Pas besoin de les paramétrer vous-même : on le fait pour vous.
          </p>
          <p className="mb-4">
            💰 <strong>3. Vous ne payez que pour ce qui a de la valeur</strong> : notre <a href="/infos/tarifs" className="underline">modèle de facturation</a> est transparent, adapté aux campagnes à répétition, sans engagement inutile.
          </p>
          <p>
            En clair ? On vous fournit les bons outils, la bonne méthode et les bons contenus — et on les pilote pour vous. Vos campagnes RH deviennent un levier business, pas un casse-tête.
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default Campagnes;